import { useEffect, useState, useCallback } from 'react';
import { useAbortController } from './useAbortController';
import { useApiFetch } from './useApiFetch';
import type { ScanListItem, ScansResponse } from '../types/scans';

interface UseScansOptions {
  page?: number;
  limit?: number;
  search?: string;
}

interface UseScansResult {
  scans: ScanListItem[];
  isLoading: boolean;
  error: Error | null;
  total: number;
  page: number;
  limit: number;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  setLimit: (limit: number) => void;
  refetch: () => void;
}

export function useScans(options: UseScansOptions = {}): UseScansResult {
  const {
    page: initialPage = 1,
    limit: initialLimit = 50,
    search: initialSearch = '',
  } = options;

  const [scans, setScans] = useState<ScanListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [search, setSearch] = useState(initialSearch);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const { isLoading, error, apiFetch } = useApiFetch();
  const { getAbortController } = useAbortController();

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    const fetchScans = async () => {
      const abortController = getAbortController();

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      const response = await apiFetch(`/meta/scans?${params}`, {
        signal: abortController.signal,
      });

      if (!response) return;

      const data: ScansResponse = await response.json();

      setScans(data.items);
      setTotal(data.total);
    };

    fetchScans();
  }, [page, limit, search, refetchTrigger, apiFetch, getAbortController]);

  return {
    scans,
    isLoading,
    error,
    total,
    page,
    limit,
    setPage,
    setSearch,
    setLimit,
    refetch,
  };
}
